import { format, parseISO } from "date-fns";
import { useTranslation } from "react-i18next";
import { useDateContext } from "@/components/providers/date-provider";
import { Badge } from "@/components/ui/badge";
import type { RelocationConflict } from "@/lib/therapist-relocation";

// Booked visits a scheduled move breaks. Go already gives the day and time in WIB, so
// neither is shifted by a zone here.
export function ConflictList({
	conflicts,
}: {
	conflicts: RelocationConflict[];
}) {
	const { t } = useTranslation("therapists", {
		keyPrefix: "relocation.schedule.conflicts",
	});
	const { locale, timeFormatDateFns } = useDateContext();

	return (
		<ul className="grid border divide-y">
			{conflicts.map((conflict) => (
				<li
					key={conflict.appointmentId}
					className="grid min-w-0 gap-1 p-3 text-sm"
				>
					<div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
						<span className="font-mono text-xs font-semibold">
							{conflict.registrationNumber}
						</span>
						<span className="text-xs text-muted-foreground">
							{t("visit", { number: conflict.visitNumber })}
						</span>
						<span className="min-w-0 font-medium break-words">
							{conflict.patientName}
						</span>
					</div>
					<p className="text-xs text-muted-foreground tabular-nums">
						{format(
							parseISO(`${conflict.visitDate}T${conflict.visitTime}`),
							`PP, ${timeFormatDateFns}`,
							{ locale },
						)}
						{conflict.visitCity && ` · ${conflict.visitCity}`}
					</p>
					<div className="flex flex-wrap items-center gap-1.5">
						{conflict.kinds.map((kind) => (
							<Badge key={kind} variant="outline">
								{t(`kind.${kind}`)}
							</Badge>
						))}
						{(conflict.distanceKm != null || conflict.durationMin != null) && (
							<span className="text-xs text-muted-foreground tabular-nums">
								{[
									conflict.distanceKm != null &&
										t("distance", { km: conflict.distanceKm.toFixed(2) }),
									conflict.durationMin != null &&
										t("duration", { min: Math.round(conflict.durationMin) }),
								]
									.filter(Boolean)
									.join(" · ")}
							</span>
						)}
					</div>
				</li>
			))}
		</ul>
	);
}
